// CallLogsDrawer.tsx - Simplified and debugged version
import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Input, List, Spin, Button, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import callLogsDrawerManager from './CallLogsDrawerManager';
import { getCallLogs, CallLog } from '../services/callLogsService';
import CallLogItem from './CallLogItem';
import './CallLogsDrawer.css';
import logger from '../utils/logger';

const CallLogsDrawer: React.FC = () => {
  // Simplified state - removed complex pagination logic
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState<string>('');

  // Simplified fetch function with better error handling
  const fetchCallLogs = async (search: string = '', isRefresh: boolean = true) => {
    logger.log('🔍 fetchCallLogs called:', { search, isRefresh, loading });
    
    // Prevent multiple simultaneous calls
    if (loading) {
      logger.log('⏸️ Already loading, skipping...');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      logger.log('📡 Making API call...');
      const response = await getCallLogs({
        page: 1,
        perPage: 50, // Get more records initially
        search: search.trim() || undefined
      });

      logger.log('✅ API Response:', response);
      
      if (response && response.data) {
        setCallLogs(response.data);
        logger.log('📊 Set call logs:', response.data.length, 'items');
      } else {
        logger.log('❌ No data in response');
        setCallLogs([]);
      }

    } catch (error) {
      console.error('💥 API Error:', error);
      setError('Failed to fetch call logs. Please try again.');
      setCallLogs([]);
      
      // Show user-friendly error
      if (error instanceof Error) {
        message.error(`Error: ${error.message}`);
      } else {
        message.error('Failed to fetch call logs');
      }
    } finally {
      setLoading(false);
      logger.log('✅ fetchCallLogs completed');
    }
  };

  // Simplified debounced search
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      logger.log('🔍 Debounced search triggered:', searchTerm);
      fetchCallLogs(searchTerm);
    }, 500),
    []
  );

  // Handle drawer open
  const openDrawer = useCallback((filters?: { searchText?: string; fromDate?: string; toDate?: string }) => {
    logger.log('🔓 Opening drawer with filters:', filters);
    
    setDrawerVisible(true);
    setError('');
    
    // Set search if provided
    if (filters?.searchText) {
      setSearchText(filters.searchText);
    }
    
    // Always fetch fresh data when opening
    fetchCallLogs(filters?.searchText || '');
  }, []);

  // Handle drawer close
  const closeDrawer = useCallback(() => {
    logger.log('🔒 Closing drawer');
    setDrawerVisible(false);
    setSearchText('');
    setCallLogs([]);
    setError('');
  }, []);

  // Register with drawer manager
  useEffect(() => {
    logger.log('🔌 Registering drawer opener');
    callLogsDrawerManager.registerOpenDrawer(openDrawer);
    
    return () => {
      logger.log('🔌 Unregistering drawer opener');
      callLogsDrawerManager.unregisterOpenDrawer();
      debouncedSearch.cancel();
    };
  }, [openDrawer, debouncedSearch]);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    
    if (value.trim()) {
      debouncedSearch(value);
    } else {
      debouncedSearch.cancel();
      fetchCallLogs(''); // Fetch all when search is cleared
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    logger.log('🔄 Manual refresh triggered');
    setSearchText('');
    fetchCallLogs('');
  };

  // Skip rendering the Drawer entirely when not visible
  if (!drawerVisible) {
    return null;
  }

  return (
    <Drawer
      title="Call Logs"
      placement="right"
      onClose={closeDrawer}
      visible={drawerVisible}
      width={600}
      destroyOnClose={true}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Search Controls */}
        <div className="search-container">
          <Input
            placeholder="Search by name or phone number"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearchChange}
            allowClear
            className="search-input"
            disabled={loading}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            className="refresh-button"
            loading={loading}
          >
            Refresh
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{ 
            padding: '16px', 
            background: '#fff2f0', 
            border: '1px solid #ffccc7',
            borderRadius: '6px',
            marginBottom: '16px',
            color: '#cf1322'
          }}>
            {error}
          </div>
        )}

        {/* Content Area */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && callLogs.length === 0 ? (
            <div className="loader">
              <Spin tip="Loading call logs..." />
            </div>
          ) : callLogs.length > 0 ? (
            <List
              dataSource={callLogs}
              renderItem={(item) => (
                <CallLogItem key={item._id} log={item} />
              )}
              className="call-logs-list"
            />
          ) : (
            <div className="empty-state">
              <h4>No Call Logs Found</h4>
              <p>Try adjusting your search criteria or check your connection.</p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CallLogsDrawer;